import { LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useParams, useNavigate } from "react-router-dom";
import "../../connect/connect.css";
import { ConnectAuthProvider, useConnect } from "../../connect/store";
import { Activity } from "../../components/connect/Activity";
import { Settings } from "../../components/connect/Settings";
import { MyTraining } from "../../components/connect/MyTraining";
import { MyPay } from "../../components/connect/MyPay";
import { BottomNav } from "../../components/connect/BottomNav";
import { ChatsList } from "../../components/connect/ChatsList";
import { ChatThread } from "../../components/connect/ChatThread";
import { Directory } from "../../components/connect/Directory";
import { GroupFeed } from "../../components/connect/GroupFeed";
import { GroupPost } from "../../components/connect/GroupPost";
import { Search } from "../../components/connect/Search";
import { Profile } from "../../components/connect/Profile";
import { GroupMembers } from "../../components/connect/GroupMembers";
import { SendAs } from "../../components/connect/admin/SendAs";
import Broadcast from "../../components/connect/admin/Broadcast";
import { AdminDevices } from "../../components/connect/admin/AdminDevices";
import { AdminSessions } from "../../components/connect/admin/AdminSessions";
import { AdminDoor } from "../../components/connect/admin/AdminDoor";
import { AdminChatAccess } from "../../components/connect/admin/AdminChatAccess";
import { PunchoutPrompt } from "../../components/connect/admin/PunchoutPrompt";
import { AdminEngagement } from "../../components/connect/admin/AdminEngagement";
import { Login } from "../../components/connect/Login";
import { InstallGuide } from "../../components/connect/InstallGuide";
import { ForgotPassword, ResetPassword } from "../../components/connect/PasswordRecovery";
import { AdminHome } from "../../components/connect/admin/AdminHome";
import { AdminInbox } from "../../components/connect/admin/AdminInbox";
import { AdminPermissions } from "../../components/connect/admin/AdminPermissions";
import { AdminQueue } from "../../components/connect/admin/AdminQueue";
import { AdminSettings } from "../../components/connect/admin/AdminSettings";
import { AdminStaff } from "../../components/connect/admin/AdminStaff";
import { Avatar, CEO_ORG, CEO_TITLE, Spinner } from "../../components/connect/ui";

const Thread = () => { const { cid } = useParams(); return <ChatThread cid={cid} />; };
const AdminThread = () => { const { cid } = useParams(); return <ChatThread cid={cid} admin back="/connect/admin/inbox" />; };
const CeoOnly = ({ children }) => { const { me } = useConnect(); return me?.is_ceo ? children : <Navigate to="/connect/chats" replace />; };

const useIsDesktop = () => {
  const [d, setD] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => { const mq = window.matchMedia("(min-width: 1024px)"); const h = (e) => setD(e.matches); mq.addEventListener("change", h); return () => mq.removeEventListener("change", h); }, []);
  return d;
};

const sectionOf = (p) => (p.startsWith("/connect/admin") ? "admin" : /^\/connect\/(directory|staff|search)/.test(p) ? "directory" : "chats");
const SIDEBAR = { chats: ChatsList, directory: Directory, admin: AdminHome };

/** Reload the SPA when a newer build is live (open tabs otherwise keep running the old bundle for days). */
const useFreshBuild = () => {
  useEffect(() => {
    const current = [...document.scripts].map((s) => s.src).find((s) => /\/static\/js\/main\./.test(s));
    if (!current) return undefined;
    const check = () => fetch(`/connect?_=${Date.now()}`, { cache: "no-store" }).then((r) => r.text()).then((html) => {
      const m = html.match(/\/static\/js\/main\.[a-z0-9]+\.js/);
      if (m && !current.endsWith(m[0])) window.location.reload();
    }).catch(() => {});
    const t = setInterval(check, 3 * 60 * 1000);
    document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && check());
    return () => clearInterval(t);
  }, []);
};
const LIST_ONLY = new Set(["/connect/chats", "/connect/directory", "/connect/admin"]);

/** Desktop (≥1024px): WhatsApp-Web style — icon rail · list sidebar · active conversation / page. */
const DesktopShell = ({ pathname }) => {
  const { me } = useConnect();
  const section = sectionOf(pathname);
  const Side = section === "directory" && me?.directory_hidden ? ChatsList : SIDEBAR[section];
  const placeholder = LIST_ONLY.has(pathname);
  return (
    <div className="flex-1 min-h-0 flex" data-testid="desktop-layout">
      <BottomNav vertical />
      <aside className="w-[400px] xl:w-[440px] shrink-0 border-r border-slate-800/80 flex flex-col min-h-0 bg-[#0a111e]/60" data-testid="desktop-sidebar"><Side /></aside>
      <section className="flex-1 min-w-0 flex flex-col min-h-0 relative" data-testid="desktop-main">
        {placeholder ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-10 nxc-fade-in" data-testid="desktop-placeholder">
            <img src="/nsconnect/logo-512.png" alt="" className="w-24 h-24 rounded-full opacity-90 shadow-[0_0_0_6px_rgba(245,196,81,0.15)]" />
            <h2 className="nxc-display text-2xl font-semibold mt-6 text-slate-100">NexSecure Connect</h2>
            <p className="text-sm text-slate-400 mt-2 max-w-sm">{section === "directory" ? "Pick a colleague on the left to view their profile or start a chat." : section === "admin" ? "Choose a CEO Admin section on the left." : "Select a conversation on the left to start messaging. Your messages are stored on the NexSecure server."}</p>
          </div>
        ) : <AppRoutes />}
      </section>
    </div>
  );
};

const usePwa = () => {
  useEffect(() => {
    document.title = "NexSecure Connect";
    const link = document.createElement("link");
    link.rel = "manifest"; link.href = "/nsconnect/manifest.json";
    document.head.appendChild(link);
    const theme = document.querySelector('meta[name="theme-color"]');
    const prev = theme?.getAttribute("content");
    theme?.setAttribute("content", "#070b14");
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/connect-sw.js", { scope: "/connect" }).catch(() => {});
    return () => { link.remove(); if (prev) theme?.setAttribute("content", prev); };
  }, []);
};

const Shell = () => {
  const { me, logout } = useConnect();
  useFreshBuild();
  const nav = useNavigate();
  const signOut = () => { if (window.confirm("Sign out of NexSecure Connect on this device?")) { logout(); nav("/connect", { replace: true }); } };
  const { pathname } = useLocation();
  const desktop = useIsDesktop();
  const hideNav = /^\/connect\/(chat|admin\/inbox|group\/post|staff)\/[^/]+$/.test(pathname) || pathname === "/connect/group/members" || pathname === "/connect/search";
  if (pathname === "/connect/install") return <InstallGuide />;
  if (pathname === "/connect/forgot") return <ForgotPassword />;
  if (pathname === "/connect/reset") return <ResetPassword />;
  if (me === undefined) return <div className="h-full flex items-center justify-center"><Spinner /></div>;
  if (!me) return <div className="h-full overflow-y-auto"><Login /></div>;
  return (
    <>
      <div className="shrink-0 bg-[#070b14] border-b border-white/5 px-3 py-1.5 flex items-center gap-3" data-testid="brand-bar">
        <span className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-amber-300/90 font-semibold shrink-0"><img src="/nsconnect/logo-512.png" alt="" className="w-4 h-4 rounded-full" /> <span className="hidden sm:inline">NexSecure Technologies</span><span className="sm:hidden">NexSecure</span></span>
        <Link to={`/connect/staff/${me.id}`} className="ml-auto flex items-center gap-2 min-w-0 rounded-full pl-1 pr-2.5 py-0.5 hover:bg-white/5 transition-colors" title="Logged in as — open my profile" data-testid="logged-in-as">
          <Avatar name={me.name} src={me.avatar} size={26} ceo={me.is_ceo} />
          <span className="min-w-0 text-right leading-tight">
            <span className="block text-[12px] font-semibold text-slate-100 truncate" data-testid="logged-in-name">{me.name}</span>
            <span className={`block text-[10px] truncate ${me.is_ceo ? "text-amber-300" : "text-slate-400"}`} data-testid="logged-in-role">{me.is_ceo ? `${CEO_TITLE} · ${CEO_ORG}` : `${me.title} · ${me.department}`}</span>
          </span>
        </Link>
        <button onClick={signOut} className="shrink-0 flex items-center gap-1.5 h-8 px-2.5 rounded-full border border-white/10 text-[11px] text-slate-300 hover:bg-red-500/15 hover:text-red-200 hover:border-red-400/30 transition-colors" title="Sign out" aria-label="Sign out" data-testid="sign-out-button"><LogOut size={14} /><span className="hidden sm:inline">Sign out</span></button>
      </div>
      <PunchoutPrompt />
      {desktop ? <DesktopShell pathname={pathname} /> : (<>
      <main className="flex-1 min-h-0"><AppRoutes /></main>
      {!hideNav && <BottomNav />}
      </>)}
    </>
  );
};

const DirectoryGate = () => {
  const { me } = useConnect();
  return me?.directory_hidden ? <Navigate to="/connect/chats" replace /> : <Directory />;
};

const AppRoutes = () => (
        <Routes>
          <Route index element={<Navigate to="/connect/chats" replace />} />
          <Route path="chats" element={<ChatsList />} />
          <Route path="chat/:cid" element={<Thread />} />
          <Route path="directory" element={<DirectoryGate />} />
          <Route path="group" element={<GroupFeed />} />
          <Route path="group/post/:pid" element={<GroupPost />} />
          <Route path="group/members" element={<GroupMembers />} />
          <Route path="search" element={<Search />} />
          <Route path="staff/:id" element={<Profile />} />
          <Route path="admin/devices" element={<CeoOnly><AdminDevices /></CeoOnly>} />
          <Route path="admin/sessions" element={<CeoOnly><AdminSessions /></CeoOnly>} />
          <Route path="admin/door" element={<CeoOnly><AdminDoor /></CeoOnly>} />
          <Route path="admin/engagement" element={<CeoOnly><AdminEngagement /></CeoOnly>} />
          <Route path="activity" element={<Activity />} />
          <Route path="settings" element={<Settings />} />
          <Route path="training" element={<MyTraining />} />
          <Route path="pay" element={<MyPay />} />
          <Route path="admin" element={<CeoOnly><AdminHome /></CeoOnly>} />
          <Route path="admin/inbox" element={<CeoOnly><AdminInbox mode="puppet" /></CeoOnly>} />
          <Route path="admin/inbox/:cid" element={<CeoOnly><AdminThread /></CeoOnly>} />
          <Route path="admin/conversations" element={<CeoOnly><AdminInbox mode="all" /></CeoOnly>} />
          <Route path="admin/queue" element={<CeoOnly><AdminQueue /></CeoOnly>} />
          <Route path="admin/staff" element={<CeoOnly><AdminStaff /></CeoOnly>} />
          <Route path="admin/permissions" element={<CeoOnly><AdminPermissions /></CeoOnly>} />
          <Route path="admin/settings" element={<CeoOnly><AdminSettings /></CeoOnly>} />
          <Route path="admin/chat-access" element={<CeoOnly><AdminChatAccess /></CeoOnly>} />
          <Route path="admin/send-as" element={<CeoOnly><SendAs /></CeoOnly>} />
          <Route path="admin/broadcast" element={<CeoOnly><Broadcast /></CeoOnly>} />
          <Route path="*" element={<Navigate to="/connect/chats" replace />} />
        </Routes>
);

export default function ConnectApp() {
  usePwa();
  return (
    <ConnectAuthProvider>
      <div className="nxc fixed inset-0 flex justify-center" data-testid="connect-app">
        <div className="w-full max-w-md lg:max-w-none h-full flex flex-col bg-[#070b14] border-x lg:border-x-0 border-slate-800/40 shadow-2xl relative">
          <Shell />
        </div>
      </div>
    </ConnectAuthProvider>
  );
}
